import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  ButtonGroup,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Room as RoomIcon, OpenInNew as OpenInNewIcon, Search as SearchIcon, MyLocation as MyLocationIcon } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const formatDistance = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
const formatDuration = (s) => {
  const h = Math.floor(s / 3600);
  const min = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h${min.toString().padStart(2, "0")}` : `${min} min`;
};

// Modal de roteirizacao - pedido do usuario 2026-09-16 ("criar uma
// roteirizacao das leads daquela regiao"). Dois providers escolhidos
// pelo usuario na hora (ele pediu os dois construidos: "OSRM" gratuito
// e "Google" pago, decide depois qual usar por padrao) - ver
// RouteOptimizationService no backend. "Leads no caminho" so' verifica
// o que ja esta salvo no board (gratuito, decisao explicita do
// usuario - nunca dispara busca nova na CNPJa).
const RouteModal = ({ open, onClose, points, boardId }) => {
  const [provider, setProvider] = useState("osrm");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [nearbyLeads, setNearbyLeads] = useState(null);
  const [loadingNearby, setLoadingNearby] = useState(false);

  // Pedido do usuario 2026-09-21: a rota deve comecar de onde o tecnico
  // esta agora, nao so' entre os leads selecionados. Pega a localizacao
  // via navigator.geolocation ao abrir o modal e, se disponivel, manda
  // como o PRIMEIRO ponto pro RouteOptimizationService (que ja' trata
  // points[0] como origem nos dois providers) - com lat/lon direto,
  // nao precisa geocodificar esse ponto.
  const [useCurrentLocation, setUseCurrentLocation] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locatingUser, setLocatingUser] = useState(false);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    if (!open || !useCurrentLocation || currentLocation || !navigator.geolocation) return;
    setLocatingUser(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCurrentLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLocatingUser(false);
      },
      () => {
        setLocationError("Não foi possível obter sua localização atual - a rota vai considerar só os pontos selecionados.");
        setLocatingUser(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [open, useCurrentLocation, currentLocation]);

  const handleClose = () => {
    setResult(null);
    setNearbyLeads(null);
    onClose();
  };

  const handleCalculate = async () => {
    setLoading(true);
    setResult(null);
    setNearbyLeads(null);
    try {
      const routePoints =
        useCurrentLocation && currentLocation
          ? [
              {
                label: "Você (localização atual)",
                address: `${currentLocation.lat},${currentLocation.lon}`,
                lat: currentLocation.lat,
                lon: currentLocation.lon,
              },
              ...points,
            ]
          : points;
      const { data } = await api.post("/route-optimization", { provider, points: routePoints });
      setResult(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFindNearbyLeads = async () => {
    if (!result) return;
    setLoadingNearby(true);
    try {
      const routePoints = result.orderedPoints.map((p) => ({ lat: p.lat, lon: p.lon }));
      const excludeCardIds = points.map((p) => p.cardId).filter(Boolean);
      const { data } = await api.post("/route-optimization/nearby-leads", {
        boardId,
        routePoints,
        excludeCardIds,
      });
      setNearbyLeads(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingNearby(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Roteirização ({points.length} leads)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Provedor de rota
        </Typography>
        <ButtonGroup size="small" style={{ marginBottom: 16 }}>
          <Button
            variant={provider === "osrm" ? "contained" : "outlined"}
            color={provider === "osrm" ? "primary" : "default"}
            onClick={() => setProvider("osrm")}
          >
            OSRM (grátis)
          </Button>
          <Button
            variant={provider === "google" ? "contained" : "outlined"}
            color={provider === "google" ? "primary" : "default"}
            onClick={() => setProvider("google")}
          >
            Google Maps (pago)
          </Button>
        </ButtonGroup>

        {!result && (
          <>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={useCurrentLocation}
                    onChange={(e) => setUseCurrentLocation(e.target.checked)}
                  />
                }
                label="Começar da minha localização atual"
              />
              {useCurrentLocation && locatingUser && <CircularProgress size={16} />}
              {useCurrentLocation && currentLocation && !locatingUser && (
                <MyLocationIcon fontSize="small" color="primary" />
              )}
            </Box>
            {useCurrentLocation && locationError && (
              <Typography variant="caption" color="error" display="block" style={{ marginBottom: 8 }}>
                {locationError}
              </Typography>
            )}
            <Box textAlign="center" my={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCalculate}
                disabled={loading || (useCurrentLocation && locatingUser)}
              >
                {loading ? <CircularProgress size={20} /> : "Calcular rota"}
              </Button>
            </Box>
          </>
        )}

        {result && (
          <>
            <Box display="flex" gridGap={16} mb={2}>
              <Chip label={`${formatDistance(result.distanceMeters)}`} />
              <Chip label={`~${formatDuration(result.durationSeconds)}`} />
            </Box>
            <List dense>
              {result.orderedPoints.map((p, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <Chip size="small" label={idx + 1} />
                  </ListItemIcon>
                  <ListItemText primary={p.label} secondary={p.address} />
                </ListItem>
              ))}
            </List>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={result.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginTop: 8, marginBottom: 16 }}
            >
              Abrir rota no Google Maps
            </Button>

            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Leads salvos no caminho</Typography>
                <Button
                  size="small"
                  startIcon={loadingNearby ? <CircularProgress size={14} /> : <SearchIcon />}
                  onClick={handleFindNearbyLeads}
                  disabled={loadingNearby}
                >
                  Buscar
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Só verifica leads já salvos no Kanban (gratuito) - não busca empresas novas.
              </Typography>
              {nearbyLeads && (
                <List dense>
                  {nearbyLeads.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                      Nenhum lead salvo perto dessa rota.
                    </Typography>
                  ) : (
                    nearbyLeads.map((lead) => (
                      <ListItem key={lead.cardId}>
                        <ListItemIcon style={{ minWidth: 32 }}>
                          <RoomIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={lead.title} secondary={`${lead.address} · ${lead.distanceKm} km da rota`} />
                      </ListItem>
                    ))
                  )}
                </List>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result && (
          <Button onClick={() => setResult(null)} color="default">
            Recalcular
          </Button>
        )}
        <Button onClick={handleClose} color="secondary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RouteModal;
